// Campaign Management Component
import React, { useState, useEffect } from 'react';
import { useCampaign } from '../../context/CampaignContext';
import { useAuth } from '../../context/AuthContext';
import CampaignList from './CampaignList';
import CampaignForm from './CampaignForm';
import CampaignApproval from './CampaignApproval';
import ContentModeration from './ContentModeration';
import BannerRentalPayment from './BannerRentalPayment';
import { isAdmin } from '../../utils/roleManager';

const CampaignManagement = () => {
  const { currentUser } = useAuth();
  const { loadCampaigns } = useCampaign();
  const [activeTab, setActiveTab] = useState('list'); // list, create, edit, approval, moderation
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showModerationModal, setShowModerationModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (currentUser && isAdmin(currentUser)) {
      loadCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setActiveTab('edit');
  };

  const handleView = (campaign) => {
    setSelectedCampaign(campaign);
    setActiveTab('edit'); // Use edit tab for viewing (read-only mode)
  };

  const handleApprove = (campaign) => {
    setSelectedCampaign(campaign);
    setShowApprovalModal(true);
  };

  const handleReject = (campaign) => {
    setSelectedCampaign(campaign);
    setShowApprovalModal(true);
  };

  const handleModerate = (campaign) => {
    setSelectedCampaign(campaign);
    setShowModerationModal(true);
  };

  const handlePayment = (campaign) => {
    if (campaign.type === 'banner-rental' && campaign.rental?.paymentStatus === 'pending') {
      setSelectedCampaign(campaign);
      setShowPaymentModal(true);
    }
  };

  const handleDelete = (campaign) => {
    // Handled in CampaignList
  };

  const handleToggleActive = (campaign) => {
    // Handled in CampaignList
  };

  const handleSave = () => {
    setSelectedCampaign(null);
    setActiveTab('list');
    loadCampaigns();
  };

  const handleCancel = () => {
    setSelectedCampaign(null);
    setActiveTab('list');
  };

  if (!currentUser || !isAdmin(currentUser)) {
    return (
      <div className="p-6 text-center text-red-600">
        คุณไม่มีสิทธิ์เข้าถึงหน้านี้
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">📢 จัดการ Campaign</h1>
        {activeTab === 'list' && (
          <button
            onClick={() => {
              setSelectedCampaign(null);
              setActiveTab('create');
            }}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            + สร้าง Campaign ใหม่
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="flex space-x-4">
          <button
            onClick={() => {
              setActiveTab('list');
              setSelectedCampaign(null);
            }}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            รายการ Campaign
          </button>
          {(activeTab === 'create' || activeTab === 'edit') && (
            <button
              className={`py-2 px-4 font-medium border-b-2 transition-colors ${
                activeTab === 'create' || activeTab === 'edit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {activeTab === 'edit' ? 'แก้ไข Campaign' : 'สร้าง Campaign ใหม่'}
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'list' && (
          <CampaignList
          onEdit={handleEdit}
          onView={handleView}
          onApprove={handleApprove}
          onReject={handleReject}
          onModerate={handleModerate}
          onPayment={handlePayment}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />
      )}

      {(activeTab === 'create' || activeTab === 'edit') && (
        <CampaignForm
          campaign={selectedCampaign}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative">
            <button
              onClick={() => {
                setShowApprovalModal(false);
                setSelectedCampaign(null);
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <CampaignApproval
              campaign={selectedCampaign}
              onClose={async () => {
                setShowApprovalModal(false);
                setSelectedCampaign(null);
                // Force reload campaigns to refresh the list (without filters)
                await loadCampaigns({});
                // Force re-render by toggling tab
                setActiveTab('create');
                setTimeout(() => {
                  setActiveTab('list');
                }, 100);
              }}
            />
          </div>
        </div>
      )}

      {/* Moderation Modal */}
      {showModerationModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative">
            <button
              onClick={() => {
                setShowModerationModal(false);
                setSelectedCampaign(null);
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <ContentModeration
              campaign={selectedCampaign}
              onClose={async () => {
                setShowModerationModal(false);
                setSelectedCampaign(null);
                // Force reload campaigns to refresh the list
                await loadCampaigns({});
                // Force re-render by toggling tab
                setActiveTab('create');
                setTimeout(() => {
                  setActiveTab('list');
                }, 100);
              }}
            />
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative">
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedCampaign(null);
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <BannerRentalPayment
              campaign={selectedCampaign}
              onPaymentSuccess={() => {
                setShowPaymentModal(false);
                setSelectedCampaign(null);
                loadCampaigns();
              }}
              onCancel={() => {
                setShowPaymentModal(false);
                setSelectedCampaign(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;
